import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Clock, Package as PackageIcon } from "lucide-react";
import api from "../lib/api";
import { formatMoney } from "../lib/money";
import { GLOBAL_PRODUCT } from "../lib/productCatalog";

/**
 * LuxuryClassPicker — class-based luxury booking driven by the GLOBAL
 * product catalog (`GLOBAL_PRODUCT.luxury`). Every country sees the same
 * classes; per-country pricing is discovered via Pricing V2 and shown
 * on the card, or replaced by a "Pricing coming soon" chip when the
 * country has no rule for that class yet.
 *
 * All prices are strictly data-driven — this component owns no numbers.
 */
const CLASS_ICON = {
  luxury_4_seats: <Sparkles size={14} className="text-amber-gold" />,
  luxury_8_seats: <Sparkles size={14} className="text-amber-gold" />,
};

export default function LuxuryClassPicker({
  country,
  pickupLat,
  pickupLng,
  currency,
  vehicleClass,
  onClassChange,
  rentalType,           // "hourly" | "daily"  (daily = package)
  onRentalTypeChange,
  rentalDuration,       // hours (or exact package hours 4/8)
  onRentalDurationChange,
  expectedKm,
  onExpectedKmChange,
}) {
  const { t } = useTranslation();
  // Global product options (same in every country). Country-specific
  // pricing is fetched below and gates the on-card price display.
  const options = (GLOBAL_PRODUCT.luxury || []).map((option_code) => ({ option_code }));
  const [previews, setPreviews] = useState({});     // option_code → {hourly_rate}
  const [countryAvailable, setCountryAvailable] = useState(new Set());

  // 1. Load pricing availability for the current country. Options
  // returned here get their hourly preview populated below; the rest
  // render with a "Pricing coming soon" chip.
  useEffect(() => {
    if (!country) return;
    let cancelled = false;
    api.get(`/pricing/options/${encodeURIComponent(country)}/luxury`)
      .then((r) => {
        if (cancelled) return;
        setCountryAvailable(new Set((r.data?.options || []).map((o) => o.option_code)));
      })
      .catch(() => { if (!cancelled) setCountryAvailable(new Set()); });
    return () => { cancelled = true; };
  }, [country]);

  // 2. Preview each class at its minimum hourly config so the cards
  // show a starting rate (e.g. "from ₪350/hr"). Skip options that lack
  // country pricing — those render with "Pricing coming soon" instead.
  useEffect(() => {
    if (!country || pickupLat == null || pickupLng == null || options.length === 0) return;
    let cancelled = false;
    const next = {};
    (async () => {
      for (const opt of options) {
        if (cancelled) return;
        if (!countryAvailable.has(opt.option_code)) {
          next[opt.option_code] = { hourly_rate: null, unavailable: true };
          if (!cancelled) setPreviews({ ...next });
          continue;
        }
        try {
          const { data } = await api.post("/fare/estimate", {
            country, category: "luxury",
            pickup_lat: pickupLat, pickup_lng: pickupLng,
            luxury_vehicle_class: opt.option_code,
            luxury_rental_type: "hourly",
            luxury_rental_duration: 2,
          });
          const br = data?.breakdown || {};
          next[opt.option_code] = {
            hourly_rate: br.hourly_rate ?? null,
            min_hours:   br.hourly_min_hours ?? null,
            currency:    data?.currency,
          };
        } catch { next[opt.option_code] = { hourly_rate: null }; }
        if (!cancelled) setPreviews({ ...next });
      }
    })();
    return () => { cancelled = true; };
  }, [country, pickupLat, pickupLng, countryAvailable]);   // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPreview = previews[vehicleClass];
  const cur = currency || selectedPreview?.currency;
  const minHours = selectedPreview?.min_hours || 2;

  const isPackage = rentalType === "daily";
  const isHourly  = rentalType === "hourly";

  // With a global product catalog there is always at least one class to
  // render, so the "no classes" empty state is only reached if the
  // catalog itself is empty (defensive).
  if (options.length === 0) return null;

  return (
    <div className="mb-4" data-testid="luxury-class-picker">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">
        {t("luxury.chooseClass", "Choose a class")}
      </div>
      <div className="-mx-4 px-4">
        <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x pb-1">
          {options.map((opt) => {
            const isSel = vehicleClass === opt.option_code;
            const p = previews[opt.option_code] || {};
            const isUnavailableInCountry = countryAvailable.size > 0 && !countryAvailable.has(opt.option_code);
            const label = t(
              [`luxury.${opt.option_code}`, `serviceOptions.luxury.${opt.option_code}`],
              CLASS_ORDER_LABEL[opt.option_code] || opt.option_code
            );
            return (
              <button
                key={opt.option_code}
                type="button"
                onClick={isUnavailableInCountry ? undefined : () => onClassChange(opt.option_code)}
                disabled={isUnavailableInCountry}
                className={`snap-center flex-shrink-0 w-[140px] rounded-2xl overflow-hidden border transition-all duration-200 relative text-start p-2.5 ${
                  isUnavailableInCountry
                    ? "bg-white/[0.02] border-white/10 opacity-60 cursor-not-allowed"
                    : isSel
                      ? "bg-amber-gold/[0.10] border-amber-gold ring-2 ring-amber-gold/40 scale-[1.02]"
                      : "bg-white/[0.04] border-white/10 hover:border-white/25"
                }`}
                data-testid={`luxury-class-${opt.option_code}`}
                aria-pressed={isSel}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  {CLASS_ICON[opt.option_code] || <Sparkles size={14} className="text-amber-gold" />}
                  <div className="text-[9px] uppercase tracking-widest text-amber-gold font-black">
                    {t("luxury.with_driver", "With driver")}
                  </div>
                </div>
                <div className="text-[13px] font-bold text-white leading-tight">{label}</div>
                {isUnavailableInCountry ? (
                  <div className="mt-1.5 text-[10px] text-white/60"
                       data-testid={`luxury-class-comingsoon-${opt.option_code}`}>
                    {t("common.pricingComingSoon", "Pricing coming soon")}
                  </div>
                ) : p.hourly_rate != null ? (
                  <div className="mt-1.5 text-[11px] text-white/70 tabular-nums"
                       data-testid={`luxury-class-rate-${opt.option_code}`}>
                    {t("luxury.from", "from")} {formatMoney(p.hourly_rate, p.currency || cur)}/{t("luxury.hr", "hr")}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {vehicleClass && (
        <div className="mt-3" data-testid="luxury-billing-picker">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-2">
            {t("luxury.billing", "Billing")}
          </div>
          <div className="flex gap-2 flex-wrap">
            <BillBtn active={isHourly}
                     onClick={() => { onRentalTypeChange("hourly"); if (!rentalDuration || rentalDuration < minHours) onRentalDurationChange(minHours); }}
                     testid="luxury-billing-hourly">
              <Clock size={12} className="inline mr-1" /> {t("luxury.hourly", "Hourly")}
            </BillBtn>
            <BillBtn active={isPackage && Number(rentalDuration) === 4}
                     onClick={() => { onRentalTypeChange("daily"); onRentalDurationChange(4); }}
                     testid="luxury-billing-package-4h">
              <PackageIcon size={12} className="inline mr-1" /> {t("luxury.package_4h", "4h Package")}
            </BillBtn>
            <BillBtn active={isPackage && Number(rentalDuration) === 8}
                     onClick={() => { onRentalTypeChange("daily"); onRentalDurationChange(8); }}
                     testid="luxury-billing-package-8h">
              <PackageIcon size={12} className="inline mr-1" /> {t("luxury.package_8h", "8h Package")}
            </BillBtn>
          </div>

          {isHourly && (
            <div className="mt-3" data-testid="luxury-hours-block">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-1">
                {t("luxury.hours", "Hours")} <span className="text-white/40">· {t("luxury.min", "min")} {minHours}h</span>
              </div>
              <input
                type="number"
                min={minHours}
                max={24}
                step={1}
                value={rentalDuration || ""}
                onChange={(e) => onRentalDurationChange(Math.max(minHours, Number(e.target.value) || 0))}
                placeholder={`${minHours}`}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-amber-gold tabular-nums"
                data-testid="luxury-hours-input"
              />
            </div>
          )}

          {isPackage && (
            <div className="mt-3" data-testid="luxury-expected-km-block">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 mb-1">
                {t("luxury.expectedKm", "Expected km (optional)")}
              </div>
              <input
                type="number"
                min="0"
                max="5000"
                step="10"
                value={expectedKm || ""}
                onChange={(e) => onExpectedKmChange(Number(e.target.value) || 0)}
                placeholder={t("luxury.expectedKmPh", "km beyond included allowance are billed extra")}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-amber-gold tabular-nums"
                data-testid="luxury-expected-km-input"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Default English labels (used when i18n keys are missing for a locale).
const CLASS_ORDER_LABEL = {
  luxury_4_seats:  "Luxury · 4 seats",
  luxury_8_seats:  "Luxury · 8 seats",
  // Legacy fallbacks (still needed if a historical order references them).
  executive_sedan: "Executive Sedan",
  premium_sedan:   "Premium Sedan",
  vip_van:         "VIP Van",
};

function BillBtn({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3.5 py-2 text-xs font-semibold border transition-all ${
        active
          ? "bg-amber-gold text-obsidian border-amber-gold shadow-gold"
          : "bg-white/[0.04] text-white/80 border-white/10 hover:border-white/25"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
